import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'client/js/util/modal';
import SixDigitCodeInput from 'client/js/components/six_digit_code_input';
import { FaIcon } from 'client/js/util/layout_utils';

const VERIFICATION_EVENT_KEY = 'email_login_verification:verified';

class EmailVerificationCodeInput extends Component {
  static propTypes = {
    email: PropTypes.string,
    inline: PropTypes.bool
  };

  state = {
    code: '',
    loading: false,
    error: null,
    success: null,
    locked: false,
    remainingAttempts: 5,
    timeRemaining: 900 // 15 minutes in seconds
  };

  componentDidMount() {
    // Start countdown timer
    this.timerInterval = setInterval(() => {
      this.setState(prevState => {
        if (prevState.timeRemaining <= 0) {
          clearInterval(this.timerInterval);
          return { timeRemaining: 0 };
        }
        return { timeRemaining: prevState.timeRemaining - 1 };
      });
    }, 1000);

    window.addEventListener('storage', this.handleStorageChange);
  }

  componentWillUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
    }

    window.removeEventListener('storage', this.handleStorageChange);
  }

  handleStorageChange = (event) => {
    if (event.key !== VERIFICATION_EVENT_KEY || !event.newValue) {
      return;
    }

    try {
      const payload = JSON.parse(event.newValue);

      if (payload.verified) {
        this.handleVerificationSuccess(payload.redirect_url);
      }
    } catch {
      // Ignore malformed cross-tab messages.
    }
  };

  handleVerificationSuccess = (redirectUrl) => {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.setState({ success: true, loading: false });
    this.redirectTimeout = setTimeout(() => {
      window.location.href = redirectUrl || '/usercp';
    }, 1000);
  };

  handleCodeChange = (code) => {
    this.setState({ code: code, error: null });
  };

  handleVerify = async (code = this.state.code) => {
    const codeString = code;

    if (codeString.length !== 6) {
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const response = await fetch('/verify-login-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ code: codeString })
      });

      const data = await response.json();

      if (data.success) {
        try {
          window.localStorage.setItem(VERIFICATION_EVENT_KEY, JSON.stringify({
            verified: true,
            redirect_url: data.redirect_url,
            verified_at: Math.floor(Date.now() / 1000)
          }));
        } catch {
          // Ignore browsers where localStorage is not available.
        }

        this.handleVerificationSuccess(data.redirect_url);
      } else {
        this.setState({
          error: data.message,
          loading: false,
          locked: !!data.locked,
          remainingAttempts: data.remaining_attempts || this.state.remainingAttempts,
          timeRemaining: data.code_expired ? 0 : this.state.timeRemaining,
          code: data.locked ? this.state.code : ''
        });
      }
    } catch {
      this.setState({
        error: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
        loading: false
      });
    }
  };

  handleResend = async () => {
    this.setState({ loading: true, error: null });

    try {
      const response = await fetch('/resend-login-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      });

      const data = await response.json();

      if (data.success) {
        this.setState({
          success: false,
          loading: false,
          code: '',
          timeRemaining: 900,
          locked: false,
          remainingAttempts: 5,
          error: null
        });
        if (this.inputRefs[0]) {
          this.inputRefs[0].focus();
        }
      } else {
        this.setState({ error: data.message, loading: false });
      }
    } catch {
      this.setState({
        error: 'Ein Fehler ist aufgetreten.',
        loading: false
      });
    }
  };

  maskEmail = (email) => {
    if (!email) return 'Deine E-Mail-Adresse';

    const [localPart, domain] = email.split('@');
    if (!domain) return email;

    const maskedLocal = localPart.length > 2
      ? localPart[0] + '***' + localPart[localPart.length - 1]
      : localPart[0] + '***';

    return `${maskedLocal}@${domain}`;
  };

  formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  wrapContent = (content, contentLabel) => {
    if (this.props.inline) {
      return (
        <div className="email-verification-inline">
          {content}
        </div>
      );
    }

    return (
      <Modal isOpen={true} contentLabel={contentLabel}>
        {content}
      </Modal>
    );
  };

  render() {
    const { code, loading, error, success, locked, remainingAttempts, timeRemaining } = this.state;
    const { email } = this.props;

    const isExpired = timeRemaining <= 0;
    const isLocked = locked;
    const canResend = !isLocked && (isExpired || timeRemaining <= 840);
    const canVerify = code.length === 6 && !isExpired && !isLocked && !loading;

    // Success state
    if (success) {
      return this.wrapContent(
        <div className="modal-body text-center">
          <FaIcon name="check-circle fa-4x mb-2 text-success" />
          <p className="text-muted">Du wirst weitergeleitet...</p>
        </div>,
        'Gerät verifiziert'
      );
    }

    // Main verification form
    return this.wrapContent(
      <>
        <div className="modal-body">
          <div className="text-center mb-3">
            <p className="mb-2">
              Wir haben einen Sicherheitscode an<br/>
              <strong>{this.maskEmail(email)}</strong> gesendet
            </p>

            {!isExpired && !isLocked && (
              <div className="alert alert-info d-inline-block py-1 px-3 mb-3">
                <FaIcon name="clock-o" /> Code gültig für <strong>{this.formatTime(timeRemaining)}</strong>
              </div>
            )}

            {isExpired && (
              <div className="alert alert-warning">
                <strong><FaIcon name="exclamation-triangle" /> Der Code ist abgelaufen</strong>
              </div>
            )}
          </div>

          {error && (
            <div className="alert alert-danger">
              <strong><FaIcon name="exclamation-circle" /> {error}</strong>
              {remainingAttempts > 0 && remainingAttempts < 5 && (
                <div className="mt-2 small">
                  Noch {remainingAttempts} Versuch{remainingAttempts !== 1 && 'e'} übrig
                </div>
              )}
            </div>
          )}

          <p className="text-center mb-3"><strong>Gib den 6-stelligen Code ein:</strong></p>

          <SixDigitCodeInput
            value={code}
            onChange={this.handleCodeChange}
            onComplete={this.handleVerify}
            disabled={isExpired || isLocked || loading}
            className="d-flex justify-content-center mb-3"
          />

          <p className="text-center text-muted small mb-0">
            <FaIcon name="lightbulb-o" /> Tipp: Du kannst den Code auch kopieren & einfügen
          </p>

          {canResend && !isExpired && (
            <p className="text-center small mt-3 mb-0">
              Code nicht erhalten?{' '}
              <button
                type="button"
                className="btn btn-link btn-sm p-0 align-baseline"
                onClick={this.handleResend}
                disabled={loading}
              >
                Erneut senden
              </button>
            </p>
          )}
        </div>

        <div className="modal-footer justify-content-end">
          {isLocked ? (
            <a className="btn btn-primary" href="/logout">
              <FaIcon name="sign-out" /> Abmelden
            </a>
          ) : isExpired ? (
            <button
              className="btn btn-primary"
              onClick={this.handleResend}
              disabled={loading}
            >
              <FaIcon name={loading ? 'spinner spin' : 'refresh'} />
              {' '}Neuen Code anfordern
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={this.handleVerify}
              disabled={!canVerify}
            >
              <FaIcon name={loading ? 'spinner spin' : 'check'} />
              {' '}Code überprüfen
            </button>
          )}
        </div>
      </>,
      'Gerät verifizieren'
    );
  }
}

export default EmailVerificationCodeInput;
